import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotification } from '@/contexts/NotificationContext';
import { getAuth } from 'firebase/auth';

export interface PortalCounts {
  approvals: number;
  handoffs: number;
  alerts: number;
  conversations: number;
}

const PORTAL_WS_URL = (import.meta as unknown as { env: Record<string, string> }).env.VITE_API_URL?.replace(/\/v1$/, '') ?? 'http://localhost:3001';
const IMPERSONATION_KEY = 'enska_support_token';

async function resolveToken(): Promise<string | null> {
  const imp = sessionStorage.getItem(IMPERSONATION_KEY);
  if (imp) return imp;
  try {
    const user = getAuth().currentUser;
    if (user) return await user.getIdToken();
  } catch { /* not signed in */ }
  return null;
}

interface PortalSocketContextValue {
  counts: PortalCounts;
  connected: boolean;
  /** Subscribe to a portal socket event. Cleans up on unmount. */
  on: (event: string, handler: (data: unknown) => void) => () => void;
}

const PortalSocketContext = createContext<PortalSocketContextValue>({
  counts: { approvals: 0, handoffs: 0, alerts: 0, conversations: 0 },
  connected: false,
  on: () => () => undefined,
});

export function PortalSocketProvider({ children }: { children: React.ReactNode }) {
  const { platformUser } = useAuth();
  const { warning, error: notifyError } = useNotification();
  const [counts, setCounts] = useState<PortalCounts>({ approvals: 0, handoffs: 0, alerts: 0, conversations: 0 });
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!platformUser) return;
    let active = true;

    async function connect() {
      const token = await resolveToken();
      if (!token || !active) return;

      const socket = io(`${PORTAL_WS_URL}/portal`, {
        auth: { token },
        transports: ['websocket'],
        reconnectionDelay: 3000,
        reconnectionAttempts: 10,
      });

      socketRef.current = socket;

      socket.on('connect', () => { if (active) setConnected(true); });
      socket.on('disconnect', () => setConnected(false));

      socket.on('counts:update', (data: PortalCounts) => setCounts(data));

      socket.on('alert:new', (alert: { title: string; message: string; severity: string; resolutionHint?: string }) => {
        const isCritical = alert.severity === 'CRITICAL' || alert.severity === 'ERROR';
        if (isCritical) notifyError(`🚨 ${alert.title}`, alert.message, alert.resolutionHint);
        else warning(`⚠ ${alert.title}`, alert.message);
      });

      socket.on('approval:new', () => {
        warning('New approval request', 'An agent is waiting for your approval to proceed.');
      });
    }

    void connect();

    return () => {
      active = false;
      socketRef.current?.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [platformUser?.id]);

  /** Attach a listener to the portal socket — returns a cleanup function */
  function on(event: string, handler: (data: unknown) => void) {
    const sock = socketRef.current;
    if (!sock) return () => undefined;
    sock.on(event, handler);
    return () => sock.off(event, handler);
  }

  return (
    <PortalSocketContext.Provider value={{ counts, connected, on }}>
      {children}
    </PortalSocketContext.Provider>
  );
}

export function usePortalSocketContext() {
  return useContext(PortalSocketContext);
}

/** Helper: subscribe to a portal socket event for a page's lifetime */
export function usePortalSocketEvent(event: string, handler: (data: unknown) => void) {
  const { on } = usePortalSocketContext();
  useEffect(() => {
    return on(event, handler);
  }, [event]);
}
